import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import EditSiteModal from "../pages/Settings/EditSiteModal";
import { isAdmin } from "../utils/permissions";

// ─── Icons ────────────────────────────────────────────────────────────────────

const IconRefresh = ({ spinning }) => (
  <svg
    className={`w-4 h-4 ${spinning ? "animate-spin" : ""}`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
    />
  </svg>
);

const IconSort = ({ direction }) => (
  <span className="inline-flex flex-col ml-1 gap-[1px]">
    <svg
      className={`w-2.5 h-2.5 transition-colors ${direction === "asc" ? "text-blue-600" : "text-slate-300"}`}
      viewBox="0 0 10 6"
      fill="currentColor"
    >
      <path d="M5 0L10 6H0z" />
    </svg>
    <svg
      className={`w-2.5 h-2.5 transition-colors ${direction === "desc" ? "text-blue-600" : "text-slate-300"}`}
      viewBox="0 0 10 6"
      fill="currentColor"
    >
      <path d="M5 6L0 0H10z" />
    </svg>
  </span>
);

const IconSearch = () => (
  <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z" />
  </svg>
);

// ─── Constants ────────────────────────────────────────────────────────────────

const COLUMNS = [
  { key: "code", label: "Code", sortable: true },
  { key: "name", label: "Name", sortable: true },
  { key: "region", label: "Region", sortable: true },
  { key: "active", label: "Active", sortable: true },
  { key: "locked", label: "Locked", sortable: true },
  { key: "action", label: "Action", sortable: false }
];

const FILTER_STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active only" },
  { value: "inactive", label: "Inactive only" },
  { value: "locked", label: "Locked only" },
];

// ─── Subcomponents ────────────────────────────────────────────────────────────

const StatusBadge = ({ active }) =>
  active ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
      Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500 border border-slate-200">
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
      Inactive
    </span>
  );

const LockBadge = ({ locked }) =>
  locked ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
      🔒 Locked
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-50 text-slate-400 border border-slate-200">
      Unlocked
    </span>
  );

const EmptyState = ({ isFiltered }) => (
  <tr>
    <td colSpan={COLUMNS.length} className="py-16 text-center">
      <p className="text-slate-400 text-sm">
        {isFiltered ? "No sites match your filters." : "No sites found."}
      </p>
      {isFiltered && (
        <p className="text-slate-400 text-xs mt-1">Try adjusting your search or status filter.</p>
      )}
    </td>
  </tr>
);

const ErrorBanner = ({ message, onRetry }) => (
  <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 mb-4">
    <span>{message}</span>
    <button
      onClick={onRetry}
      className="text-xs font-medium underline hover:no-underline shrink-0"
    >
      Retry
    </button>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SitesTable() {
  const [sites, setSites] = useState([]);
  const [maxCount, setMaxCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sort, setSort] = useState({ key: "name", direction: "asc" });
  const [editingSite, setEditingSite] = useState(null);
  const [addingSite, setAddingSite] = useState(false);

  function isButtonDisabled() {
    let res = true;

    if (maxCount === 0 || sites.length < maxCount) {
      res = false
    }

    return res;
  }

  function calcMaxSites() {
    let res = "Maximum: ";

    if (maxCount === 0) {
      res += "Unlimited";
    } else res += maxCount;

    return res;
  }

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchSites = useCallback(async () => {
    const API_BASE = window.MANTLE_CONFIG.API_URL;
    const token = localStorage.getItem('jwt');
    setLoading(true);
    setError(null);

    try {
      const { data } = await axios.get(`${API_BASE}/sites/load-all`, {
        headers: {
          'Authorization': 'Bearer ' + token,
          'x-db-name': window.MANTLE_CONFIG.TENANT_DB
        },
      });
      // Support both { data: [] } envelope and bare array responses
      setSites(Array.isArray(data) ? data : (data.data ?? []));
    } catch (err) {
      const message = err.response?.data?.message ?? err.message ?? "Failed to load sites.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMaxSiteCount = useCallback(async () => {
    const API_BASE = window.MANTLE_CONFIG.API_URL;
    const token = localStorage.getItem('jwt');

    try {
      const { data } = await axios.get(`${API_BASE}/licenses/site-count`, {
        headers: {
          'Authorization': 'Bearer ' + token,
          'x-db-name': window.MANTLE_CONFIG.TENANT_DB
        },
      });

      setMaxCount(data?.max_sites ?? 0);
    } catch (err) {
      const message = "Failed to load max sites count! " + (err.response?.data?.message ?? err.message ?? "");
      setError(message);
    }
  }, []);

  useEffect(() => {
    fetchSites();
    fetchMaxSiteCount();
  }, [fetchSites, fetchMaxSiteCount]);

  const updateSite = async (site) => {
    const API_BASE = window.MANTLE_CONFIG.API_URL;
    const DB = window.MANTLE_CONFIG.TENANT_DB;
    const token = localStorage.getItem('jwt');

    try {
      const { data } = await axios.post(`${API_BASE}/sites/update-site`, site, {
        headers: {
          'Authorization': 'Bearer ' + token,
          'x-db-name': DB
        },
      });
    } catch (err) {
      const message = err.response?.data?.message ?? err.message ?? "Failed to load sites.";
      setError(message);
    }
  };

  const addSite = async (site) => {
    const API_BASE = window.MANTLE_CONFIG.API_URL;
    const DB = window.MANTLE_CONFIG.TENANT_DB;
    const token = localStorage.getItem('jwt');

    try {
      const { data } = await axios.post(`${API_BASE}/sites/add-site`, site, {
        headers: {
          'Authorization': 'Bearer ' + token,
          'x-db-name': DB
        },
      });
    } catch (err) {
      const message = err.response?.data?.message ?? err.message ?? "Failed to add a new site.";
      setError(message);
    }
  };

  // ── Sorting ────────────────────────────────────────────────────────────────

  const handleSort = (key) => {
    setSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

  // ── Filtering + Sorting (derived) ──────────────────────────────────────────

  const processed = useMemo(() => {
    const term = search.trim().toLowerCase();

    const filtered = sites.filter((site) => {
      const matchesSearch =
        !term ||
        site.code?.toLowerCase().includes(term) ||
        site.name?.toLowerCase().includes(term) ||
        site.region?.toLowerCase().includes(term);

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && site.active && !site.locked) ||
        (statusFilter === "inactive" && !site.active) ||
        (statusFilter === "locked" && site.locked);

      return matchesSearch && matchesStatus;
    });

    return [...filtered].sort((a, b) => {
      const aVal = a[sort.key];
      const bVal = b[sort.key];

      if (typeof aVal === "boolean") {
        return sort.direction === "asc"
          ? Number(bVal) - Number(aVal)
          : Number(aVal) - Number(bVal);
      }

      const cmp = String(aVal ?? "").localeCompare(String(bVal ?? ""));
      return sort.direction === "asc" ? cmp : -cmp;
    });
  }, [sites, search, statusFilter, sort]);

  const isFiltered = search.trim() !== "" || statusFilter !== "all";

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="p-6">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="mb-6">
          {!loading && processed.length > 0 && (
            <p className="text-sm text-slate-500 mt-0.5">
              {processed.length} {processed.length === 1 ? "site" : "sites"}
              {isFiltered && ` — filtered from ${sites.length} total`}
            </p>
          )}
        </div>

        {/* Error */}
        {error && <ErrorBanner message={error} onRetry={fetchSites} />}

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">

          {/* Search */}
          <div className="relative flex-1">
            <span className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <IconSearch />
            </span>
            <input
              type="text"
              placeholder="Search by code, name, or region …"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
            />
          </div>

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-100
                       focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition cursor-pointer"
          >
            {FILTER_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          {/* Refresh */}
          <button
            onClick={fetchSites}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg
                       bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200
                       hover:bg-slate-50 dark:hover:bg-slate-700 active:bg-slate-100
                       disabled:opacity-50 disabled:cursor-not-allowed
                       focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
          >
            <IconRefresh spinning={loading} />
            Refresh
          </button>
        </div>

        {/* Table */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      onClick={() => col.sortable && handleSort(col.key)}
                      className={`px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide whitespace-nowrap select-none
                                  ${col.sortable ? "cursor-pointer hover:text-slate-700 hover:bg-slate-100 transition-colors" : ""}`}
                    >
                      <span className="inline-flex items-center">
                        {col.label}
                        {col.sortable && (
                          <IconSort
                            direction={sort.key === col.key ? sort.direction : null}
                          />
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {loading && sites.length === 0 ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      {COLUMNS.map((col) => (
                        <td key={col.key} className="px-4 py-3">
                          <div className="h-4 bg-slate-100 rounded w-3/4" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : processed.length === 0 ? (
                  <EmptyState isFiltered={isFiltered} />
                ) : (
                  processed.map((site, idx) => (
                    <tr
                      key={site.code ?? idx}
                      className="hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors"
                    >
                      <td className="px-4 py-3 font-mono text-xs text-slate-600 dark:text-slate-300 font-medium">
                        {site.code}
                      </td>
                      <td className="px-4 py-3 text-slate-800 dark:text-slate-100 font-medium">
                        {site.name}
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                        {site.region}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge active={site.active} />
                      </td>
                      <td className="px-4 py-3">
                        <LockBadge locked={site.locked} />
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setEditingSite(site)}
                          disabled={!isAdmin() || site.locked}
                          className="text-xs text-slate-500 border border-slate-200 rounded px-2.5 py-1 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed hover:text-slate-700 transition-colors font-medium">
                          Edit/Update
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* edit */}
          {editingSite && (
            <EditSiteModal
              site={editingSite}   // the site object from your data source
              onSave={(updated) => { updateSite(updated); fetchSites(); setEditingSite(null); }}
              onClose={() => setEditingSite(null)}
              codeDisabled={true}
            />
          )}
          {/* Add */}
          {addingSite && (
            <EditSiteModal
              site={null}   // the site object from your data source
              onSave={(updated) => { addSite(updated); fetchSites(); setAddingSite(false); }}
              onClose={() => setAddingSite(false)}
              codeDisabled={false}
            />
          )}

          {/* Table footer */}
          <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50 flex items-center justify-between text-xs text-slate-400">
            <span>{loading ? "Loading …" : `${processed.length} of ${sites.length} sites`}</span> <span>{calcMaxSites()}</span>
            <button
              onClick={() => setAddingSite(true)}
              disabled={!isAdmin() || isButtonDisabled()}
              className="text-xs text-green-500 rounded px-2.5 py-1 disabled:opacity-50 disabled:cursor-not-allowed hover:text-green-700 transition-colors font-medium"
            >
              Add Site
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
